import {
  Box,
  ClickAwayListener,
  Collapse,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { CountryCode } from '@/components/dataDisplay/FlagIcon'
import FlagIcon, { COUNTRY_FLAGS } from '@/components/dataDisplay/FlagIcon'
import CustomFormControl from '@/components/inputs/CustomFormControl'
import { FIELD_WIDTH } from '@/constants/layout'
import type { Portfolio } from '@/features/bidding/types/bid'

type TreePortfolioSelectorProps = Readonly<{
  portfolios: Portfolio[] | null
  value: string
  onChange: (portfolioCode: string) => void
}>

type PartnerGroup = {
  partnerCode: string
  partnerName: string
  portfolios: Portfolio[]
}

type CountryGroup = {
  countryCode: string
  partners: PartnerGroup[]
}

function buildTree(portfolios: Portfolio[]): CountryGroup[] {
  const countryMap = new Map<string, Map<string, PartnerGroup>>()

  for (const portfolio of portfolios) {
    const country = portfolio.countryCode.toLowerCase()
    if (!countryMap.has(country)) countryMap.set(country, new Map())
    const partnerMap = countryMap.get(country)!

    if (!partnerMap.has(portfolio.partnerCode)) {
      partnerMap.set(portfolio.partnerCode, {
        partnerCode: portfolio.partnerCode,
        partnerName: portfolio.partnerName,
        portfolios: [],
      })
    }
    partnerMap.get(portfolio.partnerCode)!.portfolios.push(portfolio)
  }

  return Array.from(countryMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([countryCode, partnerMap]) => ({
      countryCode,
      partners: Array.from(partnerMap.values())
        .sort((a, b) => a.partnerName.localeCompare(b.partnerName))
        .map((partner) => ({
          ...partner,
          portfolios: partner.portfolios.toSorted(
            (a, b) => a.priceArea.localeCompare(b.priceArea) || a.code.localeCompare(b.code),
          ),
        })),
    }))
}

function filterTree(
  tree: CountryGroup[],
  search: string,
): { filtered: CountryGroup[]; expandedCountries: Set<string>; expandedPartners: Set<string> } {
  const lower = search.toLowerCase()
  const expandedCountries = new Set<string>()
  const expandedPartners = new Set<string>()

  const filtered = tree
    .map((country) => {
      const filteredPartners = country.partners
        .map((partner) => {
          const filteredPortfolios = partner.portfolios.filter(
            (p) =>
              p.code.toLowerCase().includes(lower) ||
              p.name.toLowerCase().includes(lower) ||
              p.partnerName.toLowerCase().includes(lower) ||
              p.priceArea.toLowerCase().includes(lower),
          )
          if (filteredPortfolios.length > 0) {
            expandedCountries.add(country.countryCode)
            expandedPartners.add(partner.partnerCode)
            return { ...partner, portfolios: filteredPortfolios }
          }
          return null
        })
        .filter((p): p is PartnerGroup => p !== null)

      if (filteredPartners.length > 0) {
        return { ...country, partners: filteredPartners }
      }
      return null
    })
    .filter((c): c is CountryGroup => c !== null)

  return { filtered, expandedCountries, expandedPartners }
}

function TreePortfolioSelector({ portfolios, value, onChange }: TreePortfolioSelectorProps) {
  const { t } = useTranslation()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set())
  const [expandedPartners, setExpandedPartners] = useState<Set<string>>(new Set())
  const searchInputRef = useRef<HTMLInputElement>(null)
  const hoverTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  const tree = useMemo(() => buildTree(portfolios ?? []), [portfolios])

  const { displayTree, searchExpandedCountries, searchExpandedPartners } = useMemo(() => {
    if (!search) return { displayTree: tree, searchExpandedCountries: null, searchExpandedPartners: null }
    const { filtered, expandedCountries: ec, expandedPartners: ep } = filterTree(tree, search)
    return { displayTree: filtered, searchExpandedCountries: ec, searchExpandedPartners: ep }
  }, [tree, search])

  const isCountryExpanded = useCallback(
    (code: string) => searchExpandedCountries?.has(code) ?? expandedCountries.has(code),
    [searchExpandedCountries, expandedCountries],
  )

  const isPartnerExpanded = useCallback(
    (code: string) => searchExpandedPartners?.has(code) ?? expandedPartners.has(code),
    [searchExpandedPartners, expandedPartners],
  )

  const selectedPortfolio = useMemo(() => portfolios?.find((p) => p.code === value), [portfolios, value])

  const handleCountryHover = useCallback((countryCode: string) => {
    const existing = hoverTimers.current.get('country')
    if (existing) clearTimeout(existing)

    const timer = setTimeout(() => {
      setExpandedCountries(new Set([countryCode]))
      setExpandedPartners(new Set())
    }, 50)
    hoverTimers.current.set('country', timer)
  }, [])

  const handlePartnerHover = useCallback((partnerCode: string) => {
    const existing = hoverTimers.current.get('partner')
    if (existing) clearTimeout(existing)

    const timer = setTimeout(() => {
      setExpandedPartners((prev) => new Set(prev).add(partnerCode))
    }, 50)
    hoverTimers.current.set('partner', timer)
  }, [])

  const toggleSet = useCallback((key: string, setter: React.Dispatch<React.SetStateAction<Set<string>>>) => {
    setter((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const handleSelect = useCallback(
    (portfolioCode: string) => {
      onChange(portfolioCode)
      setOpen(false)
      setSearch('')
    },
    [onChange],
  )

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false)
        setSearch('')
      }}
    >
      <Box>
        <CustomFormControl variant="outlinedWhite">
          <TextField
            ref={anchorRef}
            InputProps={{
              readOnly: true,
              startAdornment: selectedPortfolio ? (
                <InputAdornment position="start">
                  <FlagIcon countryCode={selectedPortfolio.countryCode.toLowerCase() as CountryCode} />
                </InputAdornment>
              ) : undefined,
            }}
            label={t('activation_plan.portfolio')}
            placeholder={t('activation_plan.portfolio')}
            size="small"
            sx={{ minWidth: FIELD_WIDTH + 40, cursor: 'pointer' }}
            value={selectedPortfolio ? `${selectedPortfolio.code} — ${selectedPortfolio.name}` : ''}
            variant="outlined"
            onClick={() => setOpen((prev) => !prev)}
          />
        </CustomFormControl>

        <Popper anchorEl={anchorRef.current} open={open} placement="bottom-start" sx={{ zIndex: 1300 }}>
          <Paper
            sx={{
              mt: 0.5,
              minWidth: anchorRef.current?.offsetWidth ?? FIELD_WIDTH,
              width: 'max-content',
              maxHeight: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ p: 1 }}>
              <TextField
                fullWidth
                inputRef={searchInputRef}
                placeholder={t('common.navigation.search')}
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>

            <List dense sx={{ overflow: 'auto', flexGrow: 1 }}>
              {displayTree.map((country) => (
                <Box key={country.countryCode}>
                  <ListItemButton
                    onClick={() => toggleSet(country.countryCode, setExpandedCountries)}
                    onMouseEnter={() => handleCountryHover(country.countryCode)}
                  >
                    <FlagIcon countryCode={country.countryCode as CountryCode} sx={{ mr: 1 }} />
                    <ListItemText
                      primary={
                        country.countryCode in COUNTRY_FLAGS
                          ? t(`common.country.${country.countryCode as CountryIdentifier}`)
                          : country.countryCode.toUpperCase()
                      }
                    />
                  </ListItemButton>

                  <Collapse in={isCountryExpanded(country.countryCode)} timeout={300}>
                    {country.partners.map((partner) => (
                      <Box key={partner.partnerCode}>
                        <ListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => toggleSet(partner.partnerCode, setExpandedPartners)}
                          onMouseEnter={() => handlePartnerHover(partner.partnerCode)}
                        >
                          <ListItemText primary={partner.partnerName} />
                        </ListItemButton>

                        <Collapse in={isPartnerExpanded(partner.partnerCode)} timeout={300}>
                          <Box>
                            {partner.portfolios.map((portfolio) => (
                              <ListItemButton
                                key={portfolio.code}
                                selected={portfolio.code === value}
                                sx={{ pl: 6 }}
                                onClick={() => handleSelect(portfolio.code)}
                              >
                                <ListItemText
                                  primary={
                                    <>
                                      {portfolio.name}{' '}
                                      <Box component="span" sx={{ color: 'text.secondary' }}>
                                        ({portfolio.code})
                                      </Box>
                                    </>
                                  }
                                />
                              </ListItemButton>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </Collapse>
                </Box>
              ))}

              {displayTree.length === 0 && search && (
                <ListItemText primary={t('common.no_matches')} sx={{ px: 2, py: 1, color: 'text.secondary' }} />
              )}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default TreePortfolioSelector
